import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, X, Loader2, Plus, Plane, Camera, ImageIcon, Upload, Trash2, GripVertical, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders, getAuthToken } from "@/lib/firebase";
import TopNav from "@/components/TopNav";
import HostLayout from "@/components/host/HostLayout";
import SportPicker from "@/components/SportPicker";
import { useDraft } from "@/hooks/useDraft";

const TRIP_HERO_IMAGES = {
  basketball: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=1600",
  football:   "https://images.unsplash.com/photo-1517649763962-0c623066013b?w=1600",
  padel:      "https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?w=1600",
  volleyball: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=1600",
};

// Curated diverse stock portraits for testimonial avatars (i.pravatar.cc — stable URLs)
const STOCK_AVATARS = [
  "https://i.pravatar.cc/200?img=47",
  "https://i.pravatar.cc/200?img=12",
  "https://i.pravatar.cc/200?img=32",
  "https://i.pravatar.cc/200?img=5",
  "https://i.pravatar.cc/200?img=49",
  "https://i.pravatar.cc/200?img=68",
  "https://i.pravatar.cc/200?img=23",
  "https://i.pravatar.cc/200?img=14",
];

const emptyTrip = {
  sport: "padel",
  title: "",
  description: "",
  destination_city: "",
  destination_country: "",
  start_date: "",
  end_date: "",
  arrival_info: "",
  departure_info: "",
  price: 0,
  currency: "EUR",
  image_url: TRIP_HERO_IMAGES.padel,
  accommodation_description: "",
  meals_included: "",
  max_guests: 10,
  includes: [],
  excludes: [],
  skill_level: "all",
  host_contact: "",
  highlight_video_url: "",
  // Itinerary: List[{ title, description, image_url }]
  itinerary: [],
  // Testimonials: List[{ name, quote, photo }]
  testimonials: [],
  // FAQs: List[{ q, a }]
  faqs: [],
  // Room options: List[{ id, label, description, price_modifier, max_occupancy, capacity, gender_restriction, notes }]
  room_options: [],
  // Booking-flow toggles
  collect_gender: false,
  allow_mixed_rooms: false,
  // Gallery photos/videos (extra images beyond the cover). Each: {url, type}
  gallery: [],
  // Refund policy editable by the host. Shape mirrors `_trip_refund_pct` in backend.
  refund_policy: {
    full_refund_days: 60,
    partial_refund_days: 30,
    partial_refund_pct: 50,
    host_cancel_full_refund: true,
    custom_note: "",
  },
};

const uploadImage = async (file) => {
  const token = await getAuthToken();
  const body = new FormData();
  body.append("file", file);
  const res = await fetch(`${API}/upload/game-image`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });
  if (!res.ok) {
    const detail = await res.json().then(d => d.detail).catch(() => null);
    throw new Error(detail || "Upload failed");
  }
  const data = await res.json();
  return `${API}/files/${data.storage_path}`;
};

const ItineraryDayEditor = ({ index, day, onChange, onRemove }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const url = await uploadImage(f);
      onChange({ ...day, image_url: url });
      toast.success(`Day ${index + 1} image uploaded`);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="p-3 rounded-xl bg-muted/40 space-y-2" data-testid={`itinerary-day-editor-${index}`}>
      <div className="flex items-center gap-2">
        <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center">{index + 1}</span>
        <Input
          value={day.title || ""}
          onChange={(e) => onChange({ ...day, title: e.target.value })}
          placeholder={`Day ${index + 1} title (e.g. Arrival & welcome dinner)`}
          data-testid={`itinerary-title-${index}`}
        />
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Remove day">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <Textarea
        rows={2}
        value={day.description || ""}
        onChange={(e) => onChange({ ...day, description: e.target.value })}
        placeholder="What happens on this day?"
        data-testid={`itinerary-desc-${index}`}
      />
      <div className="flex items-start gap-3">
        {day.image_url ? (
          <div className="relative w-28 h-20 rounded-lg overflow-hidden border border-border shrink-0">
            <img src={day.image_url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange({ ...day, image_url: "" })}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"
              aria-label="Remove image"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-28 h-20 rounded-lg border border-dashed border-border bg-card flex items-center justify-center text-muted-foreground shrink-0">
            <ImageIcon className="w-5 h-5" />
          </div>
        )}
        <div className="flex-1 space-y-1.5">
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 text-xs font-medium cursor-pointer transition-colors" data-testid={`itinerary-upload-btn-${index}`}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : (day.image_url ? "Replace image" : "Upload image")}
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
          </label>
          <Input
            value={day.image_url || ""}
            onChange={(e) => onChange({ ...day, image_url: e.target.value })}
            placeholder="…or paste an image URL"
            className="text-xs"
            data-testid={`itinerary-img-url-${index}`}
          />
        </div>
      </div>
    </div>
  );
};

const TestimonialEditor = ({ index, t, onChange, onRemove }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const url = await uploadImage(f);
      onChange({ ...t, photo: url });
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="p-3 rounded-xl bg-muted/40 space-y-2" data-testid={`testimonial-editor-${index}`}>
      <div className="flex items-center gap-2">
        <Input
          value={t.name || ""}
          onChange={(e) => onChange({ ...t, name: e.target.value })}
          placeholder="Name (e.g. Lia M.)"
        />
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Remove testimonial">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <Textarea
        rows={2}
        value={t.quote || ""}
        onChange={(e) => onChange({ ...t, quote: e.target.value })}
        placeholder="What they said about the trip…"
      />
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-1.5">Pick a stock avatar or upload your own</p>
        <div className="flex flex-wrap items-center gap-2">
          {STOCK_AVATARS.map((url) => {
            const selected = t.photo === url;
            return (
              <button
                key={url}
                type="button"
                onClick={() => onChange({ ...t, photo: url })}
                className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all ${selected ? "border-primary ring-2 ring-primary/30" : "border-transparent hover:border-border"}`}
                data-testid={`testimonial-avatar-pick-${index}-${url.split("img=")[1]}`}
                aria-label="Use stock avatar"
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
              </button>
            );
          })}
          <label className="w-10 h-10 rounded-full border-2 border-dashed border-border bg-card hover:bg-muted/40 flex items-center justify-center cursor-pointer transition-colors" data-testid={`testimonial-upload-${index}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : <Camera className="w-4 h-4 text-muted-foreground" />}
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
          </label>
          {t.photo && !STOCK_AVATARS.includes(t.photo) && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <img src={t.photo} alt="" className="w-6 h-6 rounded-full object-cover" />
              <span>Your photo</span>
              <button
                type="button"
                onClick={() => onChange({ ...t, photo: "" })}
                className="ml-1 underline hover:text-foreground"
              >
                clear
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateTripPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;
  const [form, setForm] = useState(emptyTrip);
  const [includeInput, setIncludeInput] = useState("");
  const [excludeInput, setExcludeInput] = useState("");
  const [creating, setCreating] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const { saveDraft, discardDraft, saving: savingDraft, autoSaveStatus } = useDraft("trip", {
    onLoad: (payload) => setForm({ ...emptyTrip, ...payload, refund_policy: { ...emptyTrip.refund_policy, ...(payload?.refund_policy || {}) } }),
    autoSaveData: form,
    autoSaveEnabled: !isEditMode,
  });

  // Prefill when editing an existing trip
  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/trips/${editId}`, { headers });
        if (!res.ok) {
          toast.error("Couldn't load trip to edit");
          navigate("/host");
          return;
        }
        const t = await res.json();
        if (cancelled) return;
        setForm({
          ...emptyTrip,
          ...t,
          // Coerce ISO strings to date-input format
          start_date: t.start_date ? new Date(t.start_date).toISOString().slice(0, 10) : "",
          end_date: t.end_date ? new Date(t.end_date).toISOString().slice(0, 10) : "",
          includes: t.includes || [],
          excludes: t.excludes || [],
          gallery: t.gallery || [],
          room_options: t.room_options || [],
          testimonials: t.testimonials || [],
          faqs: t.faqs || [],
          itinerary: t.itinerary || [],
          refund_policy: { ...emptyTrip.refund_policy, ...(t.refund_policy || {}) },
        });
      } catch (_) {
        toast.error("Network error");
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, isEditMode, navigate]);

  // Prefill start date from the Schedule drag-to-create flow
  useEffect(() => {
    if (isEditMode) return;
    const d = searchParams.get("date");
    if (!d) return;
    setForm((prev) => ({ ...prev, start_date: d }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addInclude = () => {
    const v = includeInput.trim();
    if (!v) return;
    if (form.includes.includes(v)) return;
    setForm({ ...form, includes: [...form.includes, v] });
    setIncludeInput("");
  };

  const removeInclude = (val) => setForm({ ...form, includes: form.includes.filter(x => x !== val) });

  const addExclude = () => {
    const v = excludeInput.trim();
    if (!v) return;
    if (form.excludes.includes(v)) return;
    setForm({ ...form, excludes: [...form.excludes, v] });
    setExcludeInput("");
  };

  const removeExclude = (val) => setForm({ ...form, excludes: form.excludes.filter(x => x !== val) });

  const addRoomOption = () => setForm({
    ...form,
    room_options: [...form.room_options, {
      id: `room_${Date.now()}_${form.room_options.length}`,
      label: "",
      description: "",
      price_modifier: 0,
      max_occupancy: 2,
      capacity: 4,
      gender_restriction: "any",
      notes: "",
    }],
  });
  const updateRoomOption = (i, next) => {
    const arr = [...form.room_options]; arr[i] = next;
    setForm({ ...form, room_options: arr });
  };
  const removeRoomOption = (i) => setForm({ ...form, room_options: form.room_options.filter((_, idx) => idx !== i) });

  const setSport = (sport) => setForm(prev => ({ ...prev, sport, image_url: TRIP_HERO_IMAGES[sport] || prev.image_url }));

  const updateItineraryDay = (i, next) => {
    const arr = [...form.itinerary]; arr[i] = next;
    setForm({ ...form, itinerary: arr });
  };
  const removeItineraryDay = (i) => setForm({ ...form, itinerary: form.itinerary.filter((_, idx) => idx !== i) });
  const addItineraryDay = () => setForm({ ...form, itinerary: [...form.itinerary, { title: "", description: "", image_url: "" }] });

  const updateTestimonial = (i, next) => {
    const arr = [...form.testimonials]; arr[i] = next;
    setForm({ ...form, testimonials: arr });  };
  const removeTestimonial = (i) => setForm({ ...form, testimonials: form.testimonials.filter((_, idx) => idx !== i) });

  const handleSubmit = async () => {
    if (creating) return;
    if (!form.title || !form.destination_city || !form.destination_country || !form.start_date || !form.end_date || !form.price) {
      toast.error("Fill in title, destination, dates and price");
      return;
    }
    setCreating(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      // Auto-derive the cover image from the first gallery photo; fall back to
      // the default sport image when the host hasn't added any media yet.
      const firstPhoto = (form.gallery || []).find(g => g && g.url && g.type === "photo");
      const coverUrl = firstPhoto?.url || TRIP_HERO_IMAGES[form.sport] || form.image_url;
      const payload = {
        sport: form.sport,
        title: form.title,
        description: form.description,
        destination_city: form.destination_city,
        destination_country: form.destination_country,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
        arrival_info: form.arrival_info || null,
        departure_info: form.departure_info || null,
        price: parseFloat(form.price) || 0,
        currency: form.currency,
        image_url: coverUrl,
        accommodation_description: form.accommodation_description || null,
        meals_included: form.meals_included || null,
        max_guests: parseInt(form.max_guests, 10) || 10,
        includes: form.includes,
        excludes: form.excludes,
        room_options: form.room_options
          .map(r => ({
            id: r.id,
            label: (r.label || "").trim(),
            description: (r.description || "").trim(),
            price_modifier: parseFloat(r.price_modifier) || 0,
            max_occupancy: parseInt(r.max_occupancy, 10) || 1,
            capacity: parseInt(r.capacity, 10) || 0,
            gender_restriction: r.gender_restriction || "any",
            notes: (r.notes || "").trim(),
          }))
          .filter(r => r.label),
        collect_gender: !!form.collect_gender,
        allow_mixed_rooms: !!form.allow_mixed_rooms,
        skill_level: form.skill_level,
        host_name: user?.name || "Host",
        host_image_url: user?.picture || "",
        host_contact: form.host_contact || null,
        highlight_video_url: form.highlight_video_url || null,
        itinerary: form.itinerary
          .map(d => ({
            title: (d.title || "").trim(),
            description: (d.description || "").trim(),
            image_url: (d.image_url || "").trim(),
          }))
          .filter(d => d.title || d.description || d.image_url),
        testimonials: form.testimonials.filter(t => t.quote && t.quote.trim()),
        faqs: form.faqs.filter(f => f.q && f.q.trim() && f.a && f.a.trim()),
        refund_policy: {
          full_refund_days: parseInt(form.refund_policy?.full_refund_days, 10) || 60,
          partial_refund_days: parseInt(form.refund_policy?.partial_refund_days, 10) || 30,
          partial_refund_pct: Math.max(0, Math.min(100, parseInt(form.refund_policy?.partial_refund_pct, 10) || 0)),
          host_cancel_full_refund: form.refund_policy?.host_cancel_full_refund !== false,
          custom_note: (form.refund_policy?.custom_note || "").trim(),
        },
      };
      const url = isEditMode ? `${API}/trips/${editId}` : `${API}/trips`;
      const method = isEditMode ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        const data = await res.json();
        if (isEditMode) {
          toast.success("Trip updated");
          navigate(`/host/manage/trip/${data.trip_id || editId}`);
          return;
        }
        // Create-only: attach any pre-uploaded gallery media now that we have a trip_id
        const gallery = (form.gallery || []).filter(g => g && g.url);
        if (gallery.length > 0) {
          const mediaHeaders = await authHeaders({ "Content-Type": "application/json" });
          await Promise.all(gallery.map((g) =>
            fetch(`${API}/trips/${data.trip_id}/media/url`, {
              method: "POST",
              headers: mediaHeaders,
              body: JSON.stringify({ url: g.url, type: g.type || "photo", caption: g.caption || null }),
            }).catch(() => null)
          ));
        }
        toast.success(`Trip created${gallery.length ? ` with ${gallery.length} gallery item(s)` : ""}!`);
        await discardDraft();
        navigate(`/trips/${data.trip_id}`);
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || `Failed to ${isEditMode ? "update" : "create"} trip`);
      }
    } catch (e) {
      toast.error(`Failed to ${isEditMode ? "update" : "create"} trip`);
    } finally {
      setCreating(false);
    }
  };

  const fromHost = searchParams.get("from") === "host";
  const backTo = isEditMode ? `/host/manage/trip/${editId}` : (fromHost ? "/host/events" : "/profile");
  const content = (
    <>
      <div data-testid="create-trip-page">
        <button
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
          data-testid="create-trip-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {loadingEdit && (
          <div className="py-8 flex items-center justify-center gap-2 text-muted-foreground" data-testid="edit-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading trip…
          </div>
        )}

        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl flex items-center gap-2">
            <Plane className="w-7 h-7 text-primary" /> {isEditMode ? "Edit trip" : "Host a trip or retreat"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isEditMode
              ? "Update the details below. Existing bookings keep their spots."
              : "A multi-day experience. Guests book the whole thing in one go."}
          </p>
        </div>

        <div className="space-y-6">
          {/* Sport + skill */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="trip-sport">Activity type</Label>
              <SportPicker value={form.sport} onChange={setSport} testid="trip-sport" />
            </div>
            <div>
              <Label htmlFor="trip-skill">Level</Label>
              <Select value={form.skill_level} onValueChange={(v) => setForm({ ...form, skill_level: v })}>
                <SelectTrigger id="trip-skill"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="trip-title">Title *</Label>
            <Input id="trip-title" data-testid="trip-title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Padel & Paella · Marbella Long Weekend" />
          </div>

          {/* Gallery — primary image input. First photo is auto-used as the trip cover. */}
          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="trip-gallery-section">
            <div>
              <Label>Photos & videos</Label>
              <p className="text-xs text-muted-foreground">
                Add as many as you want — the first one becomes your cover and the rest fill the magazine-style gallery on the trip page.
              </p>
            </div>

            {form.gallery.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="trip-gallery-grid">
                {form.gallery.map((g, i) => (
                  <div key={i} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted/40 group" data-testid={`gallery-item-${i}`}>
                    {g.type === "video" ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-foreground/5">
                        <div className="text-center">
                          <Plane className="w-5 h-5 mx-auto mb-1 text-muted-foreground rotate-45" />
                          <p className="text-[10px] text-muted-foreground line-clamp-2 px-1">{g.url}</p>
                        </div>
                      </div>
                    ) : (
                      <img src={g.url} alt="" className="w-full h-full object-cover" />
                    )}
                    {/* Move + Delete overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors">
                      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {i > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const arr = [...form.gallery];
                              [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                              setForm({ ...form, gallery: arr });
                            }}
                            className="w-6 h-6 rounded-full bg-white/90 text-foreground flex items-center justify-center hover:bg-white"
                            aria-label="Move earlier"
                            title="Move earlier"
                          >
                            <GripVertical className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, idx) => idx !== i) })}
                          className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700"
                          aria-label="Remove"
                          data-testid={`gallery-remove-${i}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 text-xs font-medium cursor-pointer transition-colors" data-testid="gallery-upload-btn">
                <Camera className="w-3.5 h-3.5" />
                Upload photos
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    e.target.value = "";
                    const token = await getAuthToken();
                    const uploaded = await Promise.all(files.map(async (file) => {
                      try {
                        const body = new FormData();
                        body.append("file", file);
                        const r = await fetch(`${API}/upload/game-image`, {
                          method: "POST",
                          headers: token ? { Authorization: `Bearer ${token}` } : {},
                          body,
                        });
                        if (!r.ok) return null;
                        const data = await r.json();
                        return { type: "photo", url: `${API}/files/${data.storage_path}` };
                      } catch { return null; }
                    }));
                    const ok = uploaded.filter(Boolean);
                    if (ok.length > 0) {
                      setForm({ ...form, gallery: [...form.gallery, ...ok] });
                      toast.success(`${ok.length} photo${ok.length !== 1 ? "s" : ""} uploaded`);
                    }
                    const failed = uploaded.length - ok.length;
                    if (failed > 0) toast.error(`${failed} photo${failed !== 1 ? "s" : ""} failed`);
                  }}
                />
              </label>

              <button
                type="button"
                onClick={() => {
                  const url = window.prompt("Paste a YouTube / Vimeo / direct video URL");
                  if (!url || !url.trim()) return;
                  setForm({ ...form, gallery: [...form.gallery, { type: "video", url: url.trim() }] });
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 text-xs font-medium transition-colors"
                data-testid="gallery-add-video-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add video URL
              </button>

              <button
                type="button"
                onClick={() => {
                  const url = window.prompt("Paste an image URL");
                  if (!url || !url.trim()) return;
                  setForm({ ...form, gallery: [...form.gallery, { type: "photo", url: url.trim() }] });
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 text-xs font-medium transition-colors"
                data-testid="gallery-add-url-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add image URL
              </button>
            </div>

            {form.gallery.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                No gallery items yet. The cover image alone will show — add a few more to make the trip page sing.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="trip-desc">Description</Label>
            <Textarea id="trip-desc" rows={4} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="The vibe, daily structure, who it's for, what makes it special…" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="trip-city">Destination city *</Label>
              <Input id="trip-city" value={form.destination_city}
                onChange={(e) => setForm({ ...form, destination_city: e.target.value })}
                placeholder="Marbella" />
            </div>
            <div>
              <Label htmlFor="trip-country">Country *</Label>
              <Input id="trip-country" value={form.destination_country}
                onChange={(e) => setForm({ ...form, destination_country: e.target.value })}
                placeholder="Spain" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="trip-start">Start date *</Label>
              <Input id="trip-start" type="date" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                min={new Date().toISOString().split("T")[0]} />
            </div>
            <div>
              <Label htmlFor="trip-end">End date *</Label>
              <Input id="trip-end" type="date" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                min={form.start_date || new Date().toISOString().split("T")[0]} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="trip-price">Price per person *</Label>
              <Input id="trip-price" type="number" min={0} step="0.01" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="trip-currency">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger id="trip-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">€ EUR</SelectItem>
                  <SelectItem value="GBP">£ GBP</SelectItem>
                  <SelectItem value="USD">$ USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="trip-max">Max guests</Label>
              <Input id="trip-max" type="number" min={1} value={form.max_guests}
                onChange={(e) => setForm({ ...form, max_guests: e.target.value })} />
            </div>
          </div>

          {/* What's included */}
          <div className="rounded-xl border border-border p-4 space-y-2">
            <Label>What&apos;s included</Label>
            <div className="flex gap-2">
              <Input value={includeInput} onChange={(e) => setIncludeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInclude(); } }}
                placeholder="e.g. Coaching, Airport transfer, Breakfast"
                data-testid="trip-include-input" />
              <Button type="button" variant="outline" onClick={addInclude}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.includes.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {form.includes.map(v => (
                  <button key={v} type="button" onClick={() => removeInclude(v)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted hover:bg-muted/70 text-xs">
                    {v} <X className="w-3 h-3" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* What's NOT included — shown as a red-X list on the trip page */}
          <div className="rounded-xl border border-border p-4 space-y-2" data-testid="trip-excludes-section">
            <Label>What&apos;s NOT included</Label>
            <p className="text-xs text-muted-foreground -mt-1">Be transparent — list flights, gear rental, anything extra travellers should know about.</p>
            <div className="flex gap-2">
              <Input value={excludeInput} onChange={(e) => setExcludeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExclude(); } }}
                placeholder="e.g. Flights, Travel insurance, Lunch"
                data-testid="trip-exclude-input" />
              <Button type="button" variant="outline" onClick={addExclude}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.excludes.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {form.excludes.map(v => (
                  <button key={v} type="button" onClick={() => removeExclude(v)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/50 text-xs">
                    {v} <X className="w-3 h-3" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Room options — multiple pricing tiers (single, twin shared, mixed dorm…) */}
          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="trip-rooms-section">
            <div>
              <Label>Room options & pricing tiers</Label>
              <p className="text-xs text-muted-foreground">
                Offer different room types — e.g. shared dorm (€0 modifier), twin shared (+€50), private single (+€200). Travellers pick during checkout. Leave empty if you only have one room type.
              </p>
            </div>
            {form.room_options.map((room, i) => (
              <div key={i} className="p-3 rounded-xl bg-muted/40 space-y-2" data-testid={`room-option-${i}`}>
                <div className="flex items-center gap-2">
                  <Input
                    value={room.label}
                    onChange={(e) => updateRoomOption(i, { ...room, label: e.target.value })}
                    placeholder="Room label (e.g. Private single)"
                    data-testid={`room-label-${i}`}
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeRoomOption(i)} aria-label="Remove room option">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <Textarea
                  rows={2}
                  value={room.description}
                  onChange={(e) => updateRoomOption(i, { ...room, description: e.target.value })}
                  placeholder="Short description (e.g. Sea view, queen bed, en-suite bathroom)"
                />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Price modifier ({form.currency})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={room.price_modifier}
                      onChange={(e) => updateRoomOption(i, { ...room, price_modifier: e.target.value })}
                      placeholder="0"
                      data-testid={`room-price-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Capacity</Label>
                    <Input
                      type="number"
                      min={0}
                      value={room.capacity ?? 0}
                      onChange={(e) => updateRoomOption(i, { ...room, capacity: e.target.value })}
                      placeholder="Total travellers"
                      data-testid={`room-capacity-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Per-room beds</Label>
                    <Input
                      type="number"
                      min={1}
                      value={room.max_occupancy}
                      onChange={(e) => updateRoomOption(i, { ...room, max_occupancy: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Gender</Label>
                    <Select
                      value={room.gender_restriction || "any"}
                      onValueChange={(v) => updateRoomOption(i, { ...room, gender_restriction: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="female">Female only</SelectItem>
                        <SelectItem value="male">Male only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Input
                  value={room.notes || ""}
                  onChange={(e) => updateRoomOption(i, { ...room, notes: e.target.value })}
                  placeholder="Extra notes (optional, e.g. 'No extra charge — included with the trip')"
                  className="text-xs"
                />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addRoomOption} data-testid="add-room-option-btn">
              <Plus className="w-4 h-4 mr-1" /> Add room option
            </Button>

            {/* Booking-flow toggles */}
            <div className="pt-3 mt-3 border-t border-border space-y-2.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Booking flow</Label>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={form.collect_gender}
                  onCheckedChange={(v) => setForm({ ...form, collect_gender: !!v })}
                  data-testid="trip-collect-gender"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium leading-tight">Ask travellers for gender at checkout</p>
                  <p className="text-xs text-muted-foreground">Needed if you have gender-restricted rooms or want to balance the group.</p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={form.allow_mixed_rooms}
                  onCheckedChange={(v) => setForm({ ...form, allow_mixed_rooms: !!v })}
                  data-testid="trip-allow-mixed"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium leading-tight">Offer a "mixed room OK" option</p>
                  <p className="text-xs text-muted-foreground">Travellers can opt in to share a mixed-gender room with their group at no extra charge.</p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <Label htmlFor="trip-accom">Accommodation</Label>
            <Textarea id="trip-accom" rows={2} value={form.accommodation_description}
              onChange={(e) => setForm({ ...form, accommodation_description: e.target.value })}
              placeholder="Shared villa, 2 per room, ocean view…" />
          </div>

          <div>
            <Label htmlFor="trip-meals">Meals</Label>
            <Input id="trip-meals" value={form.meals_included}
              onChange={(e) => setForm({ ...form, meals_included: e.target.value })}
              placeholder="Breakfast + dinner daily" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="trip-arrival">Arrival info</Label>
              <Textarea id="trip-arrival" rows={2} value={form.arrival_info}
                onChange={(e) => setForm({ ...form, arrival_info: e.target.value })}
                placeholder="Pickup from MAGA airport on the Friday between 14-18:00…" />
            </div>
            <div>
              <Label htmlFor="trip-departure">Departure info</Label>
              <Textarea id="trip-departure" rows={2} value={form.departure_info}
                onChange={(e) => setForm({ ...form, departure_info: e.target.value })}
                placeholder="Dropoff Sunday from 10:00, last group at 16:00…" />
            </div>
          </div>

          <div>
            <Label htmlFor="trip-contact">Public host contact</Label>
            <Input id="trip-contact" value={form.host_contact}
              onChange={(e) => setForm({ ...form, host_contact: e.target.value })}
              placeholder="@yourhandle / phone / email" />
          </div>

          <div>
            <Label htmlFor="trip-video">Highlight video URL (optional)</Label>
            <Input id="trip-video" value={form.highlight_video_url}
              onChange={(e) => setForm({ ...form, highlight_video_url: e.target.value })}
              placeholder="YouTube / Vimeo / direct mp4" />
            <p className="text-xs text-muted-foreground mt-1">Embeds at the top of your trip page. You can also upload more media after creating the trip.</p>
          </div>

          {/* Day-by-day with image upload */}
          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="trip-itinerary-section">
            <div>
              <Label>Day-by-day itinerary</Label>
              <p className="text-xs text-muted-foreground">One entry per day. Add a title, description and an optional image — travellers see this as a foldable list on your trip page.</p>
            </div>
            {form.itinerary.map((day, i) => (
              <ItineraryDayEditor
                key={i}
                index={i}
                day={day}
                onChange={(next) => updateItineraryDay(i, next)}
                onRemove={() => removeItineraryDay(i)}
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addItineraryDay} data-testid="add-itinerary-day-btn">
              <Plus className="w-4 h-4 mr-1" /> Add day
            </Button>
          </div>

          {/* Testimonials with stock avatars */}
          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="trip-testimonials-section">
            <div>
              <Label>Testimonials from past travellers (optional)</Label>
              <p className="text-xs text-muted-foreground">Add quotes. Pick a stock avatar to skip the friction, or upload a real photo.</p>
            </div>
            {form.testimonials.map((t, i) => (
              <TestimonialEditor
                key={i}
                index={i}
                t={t}
                onChange={(next) => updateTestimonial(i, next)}
                onRemove={() => removeTestimonial(i)}
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, testimonials: [...form.testimonials, { name: "", quote: "", photo: STOCK_AVATARS[form.testimonials.length % STOCK_AVATARS.length] }] })} data-testid="add-testimonial-btn">
              <Plus className="w-4 h-4 mr-1" /> Add testimonial
            </Button>
          </div>

          {/* FAQs */}
          <div className="rounded-xl border border-border p-4 space-y-3">
            <Label>FAQs (optional)</Label>
            {form.faqs.map((f, i) => (
              <div key={i} className="space-y-2 p-3 rounded-lg bg-muted/40">
                <div className="flex gap-2">
                  <Input
                    value={f.q || ""}
                    onChange={(e) => {
                      const arr = [...form.faqs]; arr[i] = { ...arr[i], q: e.target.value };
                      setForm({ ...form, faqs: arr });
                    }}
                    placeholder="Question (e.g. Do I need my own gear?)"
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => {
                    setForm({ ...form, faqs: form.faqs.filter((_, idx) => idx !== i) });
                  }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <Textarea
                  rows={2}
                  value={f.a || ""}
                  onChange={(e) => {
                    const arr = [...form.faqs]; arr[i] = { ...arr[i], a: e.target.value };
                    setForm({ ...form, faqs: arr });
                  }}
                  placeholder="Answer"
                />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, faqs: [...form.faqs, { q: "", a: "" }] })}>
              <Plus className="w-4 h-4 mr-1" /> Add FAQ
            </Button>
          </div>

          <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="trip-refund-policy-editor">
            <div>
              <p className="font-medium text-foreground text-sm">Refund policy</p>
              <p className="text-xs text-muted-foreground mt-0.5">Travellers see this on the trip page. Refunds are credited to their wallet.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="refund-full-days" className="text-xs">Full refund if cancelled before</Label>
                <div className="relative">
                  <Input
                    id="refund-full-days"
                    type="number"
                    min={0}
                    value={form.refund_policy?.full_refund_days ?? 60}
                    onChange={(e) => setForm({ ...form, refund_policy: { ...form.refund_policy, full_refund_days: e.target.value } })}
                    data-testid="refund-full-days"
                    className="pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">days</span>
                </div>
              </div>
              <div>
                <Label htmlFor="refund-partial-days" className="text-xs">Partial refund if cancelled before</Label>
                <div className="relative">
                  <Input
                    id="refund-partial-days"
                    type="number"
                    min={0}
                    value={form.refund_policy?.partial_refund_days ?? 30}
                    onChange={(e) => setForm({ ...form, refund_policy: { ...form.refund_policy, partial_refund_days: e.target.value } })}
                    data-testid="refund-partial-days"
                    className="pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">days</span>
                </div>
              </div>
              <div>
                <Label htmlFor="refund-partial-pct" className="text-xs">Partial refund amount</Label>
                <div className="relative">
                  <Input
                    id="refund-partial-pct"
                    type="number"
                    min={0}
                    max={100}
                    value={form.refund_policy?.partial_refund_pct ?? 50}
                    onChange={(e) => setForm({ ...form, refund_policy: { ...form.refund_policy, partial_refund_pct: e.target.value } })}
                    data-testid="refund-partial-pct"
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="refund-host-cancel"
                checked={form.refund_policy?.host_cancel_full_refund !== false}
                onCheckedChange={(v) => setForm({ ...form, refund_policy: { ...form.refund_policy, host_cancel_full_refund: !!v } })}
                data-testid="refund-host-cancel"
              />
              <Label htmlFor="refund-host-cancel" className="text-xs leading-tight cursor-pointer">
                If I cancel the trip myself, all bookings are fully refunded to wallets (recommended).
              </Label>
            </div>
            <div>
              <Label htmlFor="refund-note" className="text-xs">Custom note (optional)</Label>
              <Textarea
                id="refund-note"
                rows={2}
                value={form.refund_policy?.custom_note || ""}
                onChange={(e) => setForm({ ...form, refund_policy: { ...form.refund_policy, custom_note: e.target.value } })}
                placeholder="e.g. Within 14 days we can issue store credit only."
                data-testid="refund-note"
              />
            </div>
            <div className="rounded-md bg-muted/40 p-2 text-[11px] text-muted-foreground leading-relaxed">
              Preview: <span className="text-foreground">Full refund if cancelled more than {form.refund_policy?.full_refund_days || 60} days before start · {form.refund_policy?.partial_refund_pct || 0}% refund {form.refund_policy?.partial_refund_days || 0}–{form.refund_policy?.full_refund_days || 0} days before · No refund within {form.refund_policy?.partial_refund_days || 0} days.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className={`fixed bottom-0 inset-x-0 border-t border-border bg-background/95 backdrop-blur z-40 ${fromHost ? "lg:left-60" : ""}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => navigate(backTo)} className="gap-2">
            <X className="w-4 h-4" /> Cancel
          </Button>
          <div className="flex items-center gap-2">
            {!isEditMode && autoSaveStatus !== "idle" && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="autosave-indicator">
                {autoSaveStatus === "saving" ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Auto-saving…</>
                ) : (
                  <>Draft saved</>
                )}
              </span>
            )}
            {!isEditMode && (
              <Button
                variant="outline"
                onClick={() => saveDraft(form)}
                disabled={savingDraft || creating}
                className="gap-2"
                data-testid="save-trip-draft-btn"
              >
                {savingDraft ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><FileText className="w-4 h-4" /> Save draft</>}
              </Button>
            )}
            <Button onClick={handleSubmit} disabled={creating || loadingEdit} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="submit-trip-btn">
              {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> {isEditMode ? "Saving…" : "Creating…"}</> : <><Save className="w-4 h-4" /> {isEditMode ? "Save changes" : "Create trip"}</>}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return fromHost
    ? <HostLayout><div className="max-w-3xl mx-auto pb-28">{content}</div></HostLayout>
    : <div className="min-h-screen bg-background"><TopNav /><main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-32">{content}</main></div>;
};

export default CreateTripPage;
